// Rich question text: a small, safe markup for bold, italic, underline, links,
// lists and paragraphs. It is stored as a string next to the plain text
// (Question.titleRich / descriptionRich) and always rendered as elements, never
// as HTML, so respondent-facing text cannot carry scripts.
//
//   **bold**  *italic*  __underline__  [text](https://example.com)
//   a blank line starts a new paragraph; a single newline is a line break
//   lines starting with "- " make a bullet list, with "1. " a numbered list
//   \* \_ \[ \] \\ \- \. \{ write the character itself
//   a block that is only {.space} is an empty line (extra space between paragraphs)
//   a paragraph or list whose first line is {.center} (or {.left}, {.right}, {.justify}) is aligned that way,
//   and one with {.small}, {.large} or {.xlarge} is drawn smaller or bigger; both can go together: {.center .large}

export interface RichSpan {
  text: string;
  bold?: true;
  italic?: true;
  underline?: true;
  href?: string;
}

export type RichAlign = 'left' | 'center' | 'right' | 'justify';

export type RichSize = 'small' | 'large' | 'xlarge';
export const RICH_SIZES: RichSize[] = ['small', 'large', 'xlarge'];
/** Font size of each step, relative to the text around it (normal is no step). */
export const RICH_SIZE_EM: Record<RichSize, string> = { small: '0.85em', large: '1.25em', xlarge: '1.6em' };

/** `align` lines up one paragraph or list, `size` draws it smaller or bigger; without them the question's own alignment and the normal size apply. */
export type RichBlock = ({ kind: 'p'; spans: RichSpan[] } | { kind: 'ul' | 'ol'; items: RichSpan[][] } | { kind: 'space' }) & { align?: RichAlign; size?: RichSize };

const STYLE_LINE = /^\{((?:\.(?:left|center|right|justify|small|large|xlarge)\s*)+)\}$/;

/** Longest rich text a question may carry (a sanity bound, like the plain title's). */
export const MAX_RICH_LENGTH = 6000;

const ESCAPABLE = '\\*_[]()-.{';

/** Links must be web or mail addresses; anything else (javascript:, data:) stays plain text. */
export function isSafeLinkUrl(url: string): boolean {
  return /^(https?:\/\/[^\s<>"']+|mailto:[^\s<>"']+)$/i.test(url);
}

type Format = Pick<RichSpan, 'bold' | 'italic' | 'underline' | 'href'>;

const sameFormat = (a: Format, b: Format) => !!a.bold === !!b.bold && !!a.italic === !!b.italic && !!a.underline === !!b.underline && a.href === b.href;

function pushSpan(out: RichSpan[], text: string, f: Format) {
  if (!text) return;
  const last = out[out.length - 1];
  if (last && sameFormat(last, f)) last.text += text;
  else out.push({ text, ...(f.bold && { bold: true as const }), ...(f.italic && { italic: true as const }), ...(f.underline && { underline: true as const }), ...(f.href && { href: f.href }) });
}

/** Is there an unescaped `marker` at or after `from`? A marker with no partner is plain text. */
function hasLater(src: string, marker: string, from: number): boolean {
  for (let k = from; k < src.length; k++) {
    if (src[k] === '\\') k++;
    else if (marker === '*' && src.startsWith('**', k)) k++; // a bold marker is not an italic one
    else if (src.startsWith(marker, k)) return true;
  }
  return false;
}

const isSpace = (c: string | undefined) => c === undefined || /\s/.test(c);

/**
 * Whether the marker of `len` characters at `k` starts or ends a style.
 * Like Markdown, an opening marker is followed by text and a closing one
 * follows text, so "2 * 3" stays a sum and not the start of italics.
 */
function togglesStyle(src: string, k: number, len: number, marker: string, on: boolean): boolean {
  if (on) return !isSpace(src[k - 1]);
  return !isSpace(src[k + len]) && hasLater(src, marker, k + len);
}

/** `[text](url)` at `at`, or null. */
function matchLink(src: string, at: number): { text: string; url: string; end: number } | null {
  let k = at + 1;
  for (; k < src.length; k++) {
    if (src[k] === '\\') k++;
    else if (src[k] === ']') break;
  }
  if (k >= src.length || src[k + 1] !== '(') return null;
  const close = src.indexOf(')', k + 2);
  if (close < 0) return null;
  const url = src.slice(k + 2, close);
  if (!isSafeLinkUrl(url)) return null;
  return { text: src.slice(at + 1, k), url, end: close + 1 };
}

function parseInline(src: string, base: Format = {}): RichSpan[] {
  const out: RichSpan[] = [];
  let buf = '';
  let bold = !!base.bold;
  let italic = !!base.italic;
  let underline = !!base.underline;
  const fmt = (): Format => ({ bold: bold || undefined, italic: italic || undefined, underline: underline || undefined, href: base.href });
  const flush = () => {
    pushSpan(out, buf, fmt());
    buf = '';
  };
  for (let k = 0; k < src.length; ) {
    const c = src[k]!;
    if (c === '\\' && k + 1 < src.length && ESCAPABLE.includes(src[k + 1]!)) {
      buf += src[k + 1];
      k += 2;
    } else if (src.startsWith('**', k) && togglesStyle(src, k, 2, '**', bold)) {
      flush();
      bold = !bold;
      k += 2;
    } else if (src.startsWith('__', k) && togglesStyle(src, k, 2, '__', underline)) {
      flush();
      underline = !underline;
      k += 2;
    } else if (c === '*' && !src.startsWith('**', k) && togglesStyle(src, k, 1, '*', italic)) {
      flush();
      italic = !italic;
      k += 1;
    } else if (c === '[' && !base.href) {
      const link = matchLink(src, k);
      if (link) {
        flush();
        for (const s of parseInline(link.text, { ...fmt(), href: link.url })) out.push(s);
        k = link.end;
      } else {
        buf += c;
        k++;
      }
    } else {
      buf += c;
      k++;
    }
  }
  flush();
  return out;
}

const BULLET = /^- /;
const NUMBER = /^\d+\. /;

/** Reads rich text into paragraphs and lists. Anything it can't make sense of is plain text. */
export function parseRich(src: string | undefined): RichBlock[] {
  if (!src) return [];
  const blocks: RichBlock[] = [];
  for (const raw of src.replace(/\r\n?/g, '\n').split(/\n{2,}/)) {
    if (raw.trim() === '{.space}') {
      blocks.push({ kind: 'space' });
      continue;
    }
    let text = raw.replace(/^\n+|\n+$/g, '');
    const first = text.split('\n', 1)[0]!.trim();
    const m = STYLE_LINE.exec(first);
    let align: RichAlign | undefined;
    let size: RichSize | undefined;
    if (m) {
      for (const token of m[1]!.split('.').map((t) => t.trim()).filter(Boolean)) {
        if (token === 'small' || token === 'large' || token === 'xlarge') size = token;
        else align = token as RichAlign;
      }
      text = text.slice(text.indexOf('\n') + 1 || text.length).replace(/^\n+/, '');
    }
    if (!text.trim() || (m && text.trim() === first)) continue;
    const lines = text.split('\n');
    const withAlign = <T extends RichBlock>(b: T): T => ({ ...b, ...(align ? { align } : {}), ...(size ? { size } : {}) });
    if (lines.every((l) => BULLET.test(l))) blocks.push(withAlign({ kind: 'ul', items: lines.map((l) => parseInline(l.replace(BULLET, ''))) }));
    else if (lines.every((l) => NUMBER.test(l))) blocks.push(withAlign({ kind: 'ol', items: lines.map((l) => parseInline(l.replace(NUMBER, ''))) }));
    else blocks.push(withAlign({ kind: 'p', spans: parseInline(text) }));
  }
  return blocks;
}

/** Writes literal text so it reads back the same (markers, brackets and list starts escaped). */
export function escapeRich(text: string): string {
  return text
    .replace(/[\\*_[\]]/g, (c) => `\\${c}`)
    .split('\n')
    .map((line) => line.replace(/^(\s*)(-)(?= )/, '$1\\-').replace(/^(\d+)(\.)(?= )/, '$1\\.').replace(/^\{(?=\.)/, '\\{'))
    .join('\n');
}

/** Spaces stay outside the markers: "**a **b" does not read back as bold "a". */
function wrapStyle(inner: string, marker: string): string {
  if (!inner.trim()) return inner;
  const lead = inner.match(/^\s*/)![0];
  const trail = inner.match(/\s*$/)![0];
  return `${lead}${marker}${inner.slice(lead.length, inner.length - trail.length)}${marker}${trail}`;
}

/** Writes spans with one pair of markers around each run of the same style, nested bold > italic > underline. */
function writeStyled(spans: RichSpan[], level = 0): string {
  const kinds = ['bold', 'italic', 'underline'] as const;
  const markers = ['**', '*', '__'];
  if (level === kinds.length) return spans.map((s) => escapeRich(s.text)).join('');
  const key = kinds[level]!;
  let out = '';
  for (let i = 0; i < spans.length; ) {
    const on = !!spans[i]![key];
    let j = i;
    while (j < spans.length && !!spans[j]![key] === on) j++;
    const inner = writeStyled(spans.slice(i, j), level + 1);
    out += on ? wrapStyle(inner, markers[level]!) : inner;
    i = j;
  }
  return out;
}

function spansToSource(spans: RichSpan[]): string {
  let out = '';
  for (let i = 0; i < spans.length; ) {
    const href = spans[i]!.href;
    let j = i;
    while (j < spans.length && spans[j]!.href === href) j++;
    const group = spans.slice(i, j);
    // Neighbouring spans of one link are written as one link.
    out += href && isSafeLinkUrl(href) ? `[${writeStyled(group)}](${href})` : writeStyled(group);
    i = j;
  }
  return out;
}

/** The markup for blocks; parseRich reads it back. */
export function serializeRich(blocks: RichBlock[]): string {
  return blocks
    .map((b) => {
      if (b.kind === 'space') return '{.space}';
      const body = b.kind === 'p' ? spansToSource(b.spans) : b.items.map((item, i) => `${b.kind === 'ul' ? '-' : `${i + 1}.`} ${spansToSource(item)}`).join('\n');
      const style = [b.align, b.size].filter(Boolean).map((x) => `.${x}`).join(' ');
      return style ? `{${style}}\n${body}` : body;
    })
    .join('\n\n');
}

/** The text without formatting: paragraphs separated by a blank line, list items on their own line. */
export function richToPlain(src: string | undefined): string {
  const flat = (spans: RichSpan[]) => spans.map((s) => s.text).join('');
  return parseRich(src)
    .flatMap((b) => (b.kind === 'space' ? [] : [b]))
    .map((b) => (b.kind === 'p' ? flat(b.spans) : b.items.map((it, i) => `${b.kind === 'ul' ? '-' : `${i + 1}.`} ${flat(it)}`).join('\n')))
    .join('\n\n')
    .trim();
}

/** True when the text has anything a plain string can't carry: styling, links, lists, paragraphs or line breaks. */
export function hasRichFormatting(blocks: RichBlock[]): boolean {
  if (blocks.length > 1) return true;
  const b = blocks[0];
  if (!b) return false;
  if (b.align || b.size) return true;
  if (b.kind !== 'p') return true;
  return b.spans.some((s) => s.bold || s.italic || s.underline || s.href || s.text.includes('\n'));
}
